import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { Popover } from "@tql/ui-components-react/kendo/tooltip";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	infoCircle,
	plusCircle,
	refreshCcw_03,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useRef, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { ColumnChooser } from "~/shared/components/column-chooser/column-chooser.tsx";
import { CustomerSearch } from "~/shared/components/customer-search/customer-search.tsx";
import { TraxCard } from "~/shared/components/trax-card/trax-card.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useAutoResizeColumns } from "~/shared/hooks/use-auto-resize-columns.ts";
import { useColumnSelections } from "~/shared/hooks/use-column-selections.ts";
import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useCustomerSelection } from "~/shared/hooks/use-customer-selection.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useTraxUsers } from "~/shared/hooks/use-trax-users.ts";
import type { TraxUser } from "~/shared/schemas/trax-user-schema.ts";
import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { AddEditUserModal } from "./components/add-user-modal/add-user-modal.tsx";
import { MobileUserManagement } from "./components/mobile-user-management.tsx";
import { useAdminColumns } from "./hooks/use-admin-columns.tsx";
import styles from "./index.module.css";

export const Admin = () => {
	const { isInternal } = useDeploymentType();
	const { data: currentUser, isLoading: isUserLoading } =
		useGetCurrentUserInfo(isInternal);
	const currentUserRole = currentUser?.role ?? "";
	const isMobile = useIsMobile();

	const { activeCustomer, handleSelectCustomer } = useCustomerSelection({
		baseRoute: routes.admin,
		customerRoute: routes.adminWithCustomer,
	});

	const customerId = activeCustomer?.id;

	const {
		users,
		isLoading,
		isFetching,
		refreshUsers,
		addUser,
		isAdding,
		updateUser,
		isUpdating,
		deleteUser,
	} = useTraxUsers(customerId, "admin");

	const { skip, take, handlePageChange, isLoadingPreference } = useGridState(
		GridIdentifiers.Admin,
	);

	const pageLoading =
		isLoading || isFetching || isUserLoading || isLoadingPreference;

	const [showAddModal, setShowAddModal] = useState(false);
	const [editingUser, setEditingUser] = useState<TraxUser | undefined>();
	const [deletingUser, setDeletingUser] = useState<TraxUser | undefined>();
	const [showLoading, setShowLoading] = useState(false);

	const handleEdit = useCallback((user: TraxUser) => {
		setEditingUser(user);
	}, []);

	const handleDelete = useCallback((user: TraxUser) => {
		setDeletingUser(user);
	}, []);

	const adminColumns = useAdminColumns({
		onEdit: handleEdit,
		onDelete: handleDelete,
		currentUserRole,
	});

	const {
		columns: selectedColumns,
		toggleColumn: handleToggleColumn,
		resetColumns: handleResetColumns,
	} = useColumnSelections("admin", adminColumns);
	const [showInfoPopover, setShowInfoPopover] = useState(false);
	const infoAnchor = useRef<HTMLDivElement>(null);

	const processedColumns = useMemo(
		() => selectedColumns.filter((col) => col.show),
		[selectedColumns],
	);

	const { wrapperRef, distributedColumns } =
		useAutoResizeColumns(processedColumns);

	const sortedUsers = useMemo(
		() =>
			[...users].sort((a, b) =>
				(a.firstName ?? "").localeCompare(b.firstName ?? ""),
			),
		[users],
	);

	const handleAddSubmit = async (data: {
		firstName: string;
		lastName: string;
		email: string;
		role: string;
	}) => {
		await addUser(data);
		setShowAddModal(false);
	};

	const handleEditSubmit = async (data: {
		firstName: string;
		lastName: string;
		email: string;
		role: string;
	}) => {
		if (!editingUser) return;
		setShowLoading(true);
		try {
			await updateUser({
				userId: editingUser.userId,
				email: data.email,
				firstName: data.firstName,
				lastName: data.lastName,
				role: data.role,
				mobilePhoneNumber: editingUser.mobilePhoneNumber,
			});
		} finally {
			setEditingUser(undefined);
			setShowLoading(false);
		}
	};

	const handleDeleteConfirm = async () => {
		if (!deletingUser) return;
		setShowLoading(true);
		try {
			await deleteUser(deletingUser.userId);
		} finally {
			setDeletingUser(undefined);
			setShowLoading(false);
		}
	};

	return (
		<TraxPageLayout>
			<TraxPageHeader showSupportMessage title={"User Management"} />
			{isMobile ? (
				<>
					<div className={styles.mobileCustomerSearch}>
						<CustomerSearch
							disabled={false}
							onCustomerChange={handleSelectCustomer}
							selectedCustomer={activeCustomer}
							wrapperStyle={{ width: "300px" }}
						/>
					</div>
					<MobileUserManagement
						currentUserRole={currentUserRole}
						loading={pageLoading}
						onDelete={handleDelete}
						onEdit={handleEdit}
						users={sortedUsers}
					/>
				</>
			) : (
				<>
					<div className={styles.headerActions}>
						<CustomerSearch
							disabled={false}
							onCustomerChange={handleSelectCustomer}
							selectedCustomer={activeCustomer}
							wrapperStyle={{ width: "300px" }}
						/>
						<div ref={infoAnchor}>
							<Button
								aria-label={"Permission level information"}
								onClick={() => {
									setShowInfoPopover(!showInfoPopover);
								}}
								svgIcon={infoCircle}
								variant={"tertiary"}
							/>
						</div>
						<Popover
							anchor={infoAnchor.current}
							animate={false}
							callout={false}
							onClose={() => {
								setShowInfoPopover(false);
							}}
							position={"bottom"}
							show={showInfoPopover}
						>
							<div className={styles.infoPopover}>
								<strong>Permission Level Access</strong>
								<ul className={styles.infoList}>
									<li>
										<strong>Admin</strong> - Users can access all pages.
									</li>
									<li>
										<strong>Operations</strong> - Users can access all pages.
									</li>
									<li>
										<strong>Finance</strong> - Users can access all pages except
										for the Reporting page.
									</li>
									<li>
										<strong>Reporting</strong> - Users can access all pages
										except for the Account Settings and Invoices pages.
									</li>
								</ul>
							</div>
						</Popover>
						<Button
							onClick={refreshUsers}
							svgIcon={refreshCcw_03}
							variant={"tertiary"}
						/>
						<ColumnChooser
							secondary
							columns={selectedColumns}
							onColumnToggle={handleToggleColumn}
							onResetColumns={handleResetColumns}
						/>
					</div>
					<TraxCard
						className={styles.adminCard}
						contentClassName={styles.adminCardContent}
						title={"User Management"}
					>
						<div ref={wrapperRef} className={styles.gridWrapper}>
							<TraxGrid
								columns={convertToGridColumnProps(distributedColumns)}
								data={sortedUsers}
								dataItemKey={"userId"}
								filterable={true}
								loading={
									isLoading ||
									isFetching ||
									isUserLoading ||
									isLoadingPreference
								}
								onPageChange={handlePageChange}
								pageable={DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS}
								skip={skip}
								sortable={true}
								style={{ height: "100%" }}
								take={take}
							/>
						</div>
						<div className={styles.saveButton}>
							<Button
								disabled={!customerId}
								onClick={() => {
									setShowAddModal(true);
								}}
								svgIcon={plusCircle}
								variant={"primary"}
							>
								Add User
							</Button>
						</div>
					</TraxCard>
				</>
			)}
			{showAddModal ? (
				<AddEditUserModal
					currentUserRole={currentUserRole}
					isPending={isAdding}
					onClose={() => {
						setShowAddModal(false);
					}}
					onSubmit={handleAddSubmit}
				/>
			) : null}

			{editingUser ? (
				<AddEditUserModal
					currentUserRole={currentUserRole}
					isPending={isUpdating}
					onClose={() => {
						setEditingUser(undefined);
					}}
					onSubmit={handleEditSubmit}
					user={editingUser}
				/>
			) : null}

			{deletingUser ? (
				<TraxModal
					cancelText={"No"}
					confirmText={"Yes"}
					onCancel={() => {
						setDeletingUser(undefined);
						setShowLoading(false);
					}}
					onClose={() => {
						setDeletingUser(undefined);
						setShowLoading(false);
					}}
					onConfirm={() => {
						void handleDeleteConfirm();
					}}
					showButtonsDisabled={showLoading}
					title={"Delete User Confirmation"}
					type={"confirmation"}
				>
					{showLoading ? (
						<div className={styles.userLoader}>
							<Loader
								size={"medium"}
								themeColor={"primary"}
								type={"infinite-spinner"}
							/>
							Deleting User
						</div>
					) : (
						"Are you sure you want to delete this user?"
					)}
				</TraxModal>
			) : null}
		</TraxPageLayout>
	);
};
